'use client';

import React, { useState, useCallback } from 'react';
import Image from 'next/image';

interface Feature {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  image?: string;
  benefits: string[];
}

interface FeaturesSectionProps {
  features?: Feature[];
  variant?: 'tabs' | 'alternating' | 'grid';
  title?: string;
  subtitle?: string;
}

const defaultFeatures: Feature[] = [
  {
    id: 'analytics',
    title: 'Advanced Analytics',
    description: 'Get deep insights into your business performance with real-time dashboards and custom reports.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    image: '/images/feature-analytics.png',
    benefits: ['Real-time data visualization', 'Custom report builder', 'Export to PDF & Excel'],
  },
  {
    id: 'automation',
    title: 'Smart Automation',
    description: 'Automate repetitive tasks and workflows to save time and reduce human error.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    image: '/images/feature-automation.png',
    benefits: ['Drag-and-drop workflow builder', 'Scheduled triggers', '200+ integrations'],
  },
  {
    id: 'collaboration',
    title: 'Team Collaboration',
    description: 'Work together seamlessly with built-in collaboration tools and real-time sync.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    image: '/images/feature-collaboration.png',
    benefits: ['Real-time editing', 'Comments & annotations', 'Version history'],
  },
  {
    id: 'security',
    title: 'Enterprise Security',
    description: 'Bank-grade security with end-to-end encryption and compliance certifications.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    image: '/images/feature-security.png',
    benefits: ['SOC 2 Type II certified', 'GDPR compliant', 'SSO & MFA support'],
  },
];

// ============ TABS VARIANT ============

function FeaturesTabs({ features = defaultFeatures, title, subtitle }: FeaturesSectionProps) {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section className="py-20 bg-white" aria-labelledby="features-tabs-title">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          {subtitle && (
            <span className="inline-block px-4 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-full mb-4">
              {subtitle}
            </span>
          )}
          <h2 id="features-tabs-title" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {title || 'Everything you need to succeed'}
          </h2>
          <p className="text-lg text-gray-600">
            Powerful features designed to help you build, grow, and scale your business with ease.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-12" role="tablist">
          {features.map((feature, index) => (
            <button
              key={feature.id}
              onClick={() => setActiveTab(index)}
              role="tab"
              aria-selected={activeTab === index}
              aria-controls={`panel-${feature.id}`}
              className={`
                flex items-center gap-2 px-5 py-3 rounded-xl font-medium text-sm transition-all duration-300
                ${activeTab === index
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }
              `}
            >
              <span className={activeTab === index ? 'text-indigo-200' : 'text-gray-400'}>
                {feature.icon}
              </span>
              {feature.title}
            </button>
          ))}
        </div>

        {/* Tab Panels */}
        <div className="relative">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              id={`panel-${feature.id}`}
              role="tabpanel"
              aria-labelledby={`tab-${feature.id}`}
              className={`
                transition-all duration-500 ease-in-out
                ${activeTab === index ? 'opacity-100 translate-y-0' : 'opacity-0 absolute inset-0 pointer-events-none'}
              `}
            >
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Content */}
                <div className="order-2 lg:order-1">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-100 text-indigo-600 mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    {feature.description}
                  </p>
                  <ul className="space-y-4">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mt-0.5">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Image Placeholder */}
                <div className="order-1 lg:order-2">
                  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gradient-to-br from-indigo-50 to-purple-50 border border-gray-100 shadow-xl">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center p-8">
                        <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-indigo-100 flex items-center justify-center text-indigo-600">
                          {feature.icon}
                        </div>
                        <p className="text-gray-400 text-sm">Feature visualization</p>
                      </div>
                    </div>
                    {/* Decorative elements */}
                    <div className="absolute top-4 right-4 w-24 h-24 bg-indigo-100 rounded-full opacity-50 blur-2xl" />
                    <div className="absolute bottom-4 left-4 w-32 h-32 bg-purple-100 rounded-full opacity-50 blur-2xl" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ ALTERNATING VARIANT ============

function FeaturesAlternating({ features = defaultFeatures, title, subtitle }: FeaturesSectionProps) {
  return (
    <section className="py-20 bg-gray-50" aria-labelledby="features-alternating-title">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          {subtitle && (
            <span className="inline-block px-4 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-full mb-4">
              {subtitle}
            </span>
          )}
          <h2 id="features-alternating-title" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {title || 'Built for modern teams'}
          </h2>
          <p className="text-lg text-gray-600">
            Discover the features that make thousands of companies choose our platform.
          </p>
        </div>

        {/* Alternating Layout */}
        <div className="space-y-20">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-dense' : ''
              }`}
            >
              {/* Image */}
              <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-white shadow-lg border border-gray-100 group">
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-2xl bg-white shadow-md flex items-center justify-center text-indigo-600 transform group-hover:scale-110 transition-transform duration-300">
                      {feature.icon}
                    </div>
                  </div>
                  {/* Floating elements */}
                  <div className="absolute top-6 right-6 w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-indigo-100 text-indigo-600 mb-5">
                  {feature.icon}
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  {feature.description}
                </p>
                <ul className="space-y-3">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <li key={benefitIndex} className="flex items-center gap-3 text-gray-700">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============ GRID VARIANT ============

function FeaturesGrid({ features = defaultFeatures, title, subtitle }: FeaturesSectionProps) {
  return (
    <section className="py-20 bg-white" aria-labelledby="features-grid-title">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          {subtitle && (
            <span className="inline-block px-4 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-full mb-4">
              {subtitle}
            </span>
          )}
          <h2 id="features-grid-title" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {title || 'Powerful features'}
          </h2>
          <p className="text-lg text-gray-600">
            All the tools you need to take your business to the next level.
          </p>
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className="group p-6 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-indigo-100/50 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:text-white transition-colors duration-300">
                {feature.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <a
            href="#features"
            className="inline-flex items-center gap-2 text-indigo-600 font-medium hover:text-indigo-700 transition-colors"
          >
            View all features
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}

// ============ MAIN COMPONENT ============

export default function FeaturesSection(props: FeaturesSectionProps) {
  const { variant = 'tabs' } = props;

  switch (variant) {
    case 'alternating':
      return <FeaturesAlternating {...props} />;
    case 'grid':
      return <FeaturesGrid {...props} />;
    case 'tabs':
    default:
      return <FeaturesTabs {...props} />;
  }
}

// Export individual components for flexible usage
export { FeaturesTabs, FeaturesAlternating, FeaturesGrid };
